import { LoaderCircle } from "lucide-react";
import { createContext, forwardRef, useContext, type InputHTMLAttributes, type JSX, type TextareaHTMLAttributes } from "react";

import { cn } from "@/lib/cn";

/** Field chrome shared by inputs, selects and textareas. Boundary is `line-strong`. */
export const fieldClass =
  "w-full rounded-control border border-control bg-surface px-2.5 text-base text-ink placeholder:text-ink-subtle transition-colors duration-fast ease-token hover:border-control-hover focus-visible:border-accent focus-visible:outline-none focus-visible:shadow-halo disabled:cursor-not-allowed disabled:border-line-strong disabled:bg-surface-muted disabled:text-ink-subtle aria-[invalid=true]:border-danger";

/**
 * The id of the visible label a form row drew. A field inside takes its name from it, so a row
 * built as a caption beside a control still names the control for a screen reader.
 */
export const FieldLabelContext = createContext<string | undefined>(undefined);

/** The name a field takes from its row, unless the caller named or tied it to a label itself. */
function useRowName(rest: { id?: string | undefined; "aria-label"?: string | undefined; "aria-labelledby"?: string | undefined }): string | undefined {
  const row = useContext(FieldLabelContext);
  return rest.id === undefined && rest["aria-label"] === undefined ? (rest["aria-labelledby"] ?? row) : rest["aria-labelledby"];
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** `true` marks the field invalid for assistive technology and paints the border. */
  invalid?: boolean | undefined;
  /** A small spinner at the right edge: the value is being checked or saved. */
  loading?: boolean | undefined;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, loading = false, ...rest },
  ref,
): JSX.Element {
  const labelledBy = useRowName(rest);
  const field = (
    <input
      ref={ref}
      aria-labelledby={labelledBy}
      aria-invalid={invalid || undefined}
      aria-busy={loading || undefined}
      className={cn(fieldClass, "h-9", loading && "pr-8", className)}
      {...rest}
    />
  );
  if (!loading) return field;
  return (
    <span className="relative block w-full">
      {field}
      <LoaderCircle className="absolute top-2.5 right-2.5 size-4 animate-spin text-ink-subtle" strokeWidth={1.5} aria-hidden="true" />
    </span>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean | undefined;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, rows = 3, ...rest },
  ref,
): JSX.Element {
  const labelledBy = useRowName(rest);
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-labelledby={labelledBy}
      aria-invalid={invalid || undefined}
      className={cn(fieldClass, "min-h-20 py-2", className)}
      {...rest}
    />
  );
});
